import { Container, CssBaseline, LinearProgress, Stack } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import type { User } from "firebase/auth";
import type { FC, PropsWithChildren } from "react";
import { useEffect } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.scss";
import { Maintenance } from "./Maintenance";
import { UI2SelectionPage } from "./UI2SelectionPage";
import { adminRoutes } from "./admin/adminRoutes";
import { ProductMap } from "./admin/products/map/ProductMapDialog";
import { useAdminFilters } from "./admin/useAdminFilters";
import AppBar from "./appbar/AppBar";
import { AuthContext } from "./auth/AuthContext";
import type { UserContext } from "./auth/auth";
import { CalculationsRoutes1 } from "./calculations/routes";
import { useCalculationsSearch } from "./calculations/useCalculationsSearch";
import { CalculationsRoutes2 } from "./calculations2/routes";
import GenericNotFound from "./components/notFound/GenericNotFound";
import CostsPage from "./costs/CostsPage";
import { IncomingTransfersPage } from "./costs/IncomingTransfersPage";
import EditCostPage from "./costs/edit/EditCostPage";
import { useCostSearch } from "./costs/useCostSearch";
import EditCustomerPage from "./customers/EditCustomerPage";
import CustomerMapPage from "./customers/map/CustomerMapPage";
import NewCustomerPage from "./customers/NewCustomerPage";
import CustomersList from "./customers/list/CustomersList";
import RequireCustomersAccess from "./customers/RequireCustomersAccess";
import { useCustomersSearch } from "./customers/list/useCustomersSearch";
import ECommerceOfferPageOld from "./eCommerce/ECommerceOfferPage";
import ECommerceOffersPageOld from "./eCommerce/ECommerceOffersPage";
import { ECommerceOfferPage } from "./eCommerce2/ECommerceOfferPage";
import ECommerceOffersPage from "./eCommerce2/ECommerceOffersPage";
import { featureFlags } from "./featureFlags/featureFlags";
import MediaBankOld from "./media/MediaBank";
import MediaBank from "./media2/MediaBank";
import PageTitle from "./misc/PageTitle";
import { ReadOnlyBanner } from "./misc/ReadOnlyBanner";
import { useAnalyticsPageView } from "./misc/useAnalyticsPageView";
import { useAuthState } from "./misc/useAuthState";
import { OrderBookingPage } from "./orderBookings/OrderBookingPage";
import OrderConfirmationPage from "./orderConfirmations/OrderConfirmationPage";
import NewOrderPage from "./orders/NewOrderPage";
import OrderPage from "./orders/OrderPage";
import OldOrdersPage from "./orders/OrdersPage";
import OrderParticipantLists from "./orders/participantLists/OrderParticipantLists";
import { useOrderSearch } from "./orders/topBar/useOrderSearch";
import OrdersPage from "./orders2/OrdersPage";
import PublicParticipantListsPage from "./publicOrders/PublicParticipantListsPage";
import { PackagesRoutes1 } from "./publicPackages/routes";
import { PackagesRoutes2 } from "./publicPackages2/routes";
import PublicRewardPage from "./publicRewards/PublicRewardPage";
import PublicRewardsList from "./publicRewards/PublicRewardsList";
import { usePublicRewardsSearch } from "./publicRewards/usePublicRewadsSearch";
import QuotePage from "./quotes/QuotePage";
import QuotesList from "./quotes/QuotesList";
import EmailPasswordSignUpOld from "./signInSignUp/EmailPasswordSignUp";
import LongChooserOld from "./signInSignUp/LoginChooser";
import ResetPasswordOld from "./signInSignUp/ResetPassword";
import EmailPasswordSignUp from "./signInSignUp2/EmailPasswordSignUp";
import LoginChooser from "./signInSignUp2/LoginChooser";
import ResetPassword from "./signInSignUp2/ResetPassword";
import TasksPage from "./tasks/TasksPage";
import { theme1, theme2 } from "./theme";
import AdminClientsList from "./customers/list2/AdminClientsList";

type UserRole = UserContext["role"];

interface WithAppUserProps {
  readonly user: User | null;
  readonly loading: boolean;
  readonly context: UserContext | null;
  readonly forbiddenRoutes: Record<UserRole, string[]>;
}

const pathsAllowedWithoutAuth = new Set(["/login", "/sign-up", "/reset-password"]);

/** Anonymous access only for `/public/orders/:orderId/participant-lists` (signed participant-list link). */
const publicParticipantListsPathRegex = /^\/public\/orders\/[^/]+\/participant-lists\/?$/;

const WithAppUser: FC<PropsWithChildren<WithAppUserProps>> = ({
  user,
  loading,
  context,
  children,
  forbiddenRoutes,
}) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (
      !user &&
      !pathsAllowedWithoutAuth.has(pathname) &&
      !pathname.startsWith("/public/packages") &&
      !publicParticipantListsPathRegex.test(pathname)
    ) {
      const redirectTo = `${pathname}${search}`;
      navigate(`login?redirectTo=${encodeURIComponent(redirectTo)}`);
    }
  }, [user, pathname, search]);

  if (loading) {
    return <LinearProgress />;
  }

  if (user && !context) {
    return <LinearProgress />;
  }

  if (context?.role && forbiddenRoutes[context.role].some((p) => pathname.startsWith(p))) {
    return <Navigate to="/not-found" replace />;
  }

  return <AuthContext.Provider value={{ user, context }}>{children}</AuthContext.Provider>;
};

const forbiddenRoutes: Record<UserRole, string[]> = {
  CUSTOMER: ["/admin", "/customers", "/costs", "/quotes"],
  ADMIN: [],
  AGENT: [],
  PROVIDER: [],
};

const MaybeContainer: FC<PropsWithChildren<{ wrap: boolean }>> = ({ wrap, children }) => {
  if (wrap) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 1, sm: 4 }, paddingBottom: 8 }}>
        {children}
      </Container>
    );
  }
  return <>{children}</>;
};

const App = () => {
  const { user, loading, context } = useAuthState();
  useAnalyticsPageView();
  const { ordersSearch, setOrdersSearch, clear: clearOrdersSearch } = useOrderSearch();
  const { costsSearch, setCostsSearch } = useCostSearch();
  const { calculationsSearch, setCalculationsSearch } = useCalculationsSearch();
  const { customersSearch, setCustomersSearch, clear: clearCustomersSearch } = useCustomersSearch(context ?? undefined);
  const { filters, handleFiltersChange } = useAdminFilters();
  const { publicRewardsSearch, setPublicRewardsSearch } = usePublicRewardsSearch();

  const role = context?.role ?? null;
  const isAdminOrAgent = role === "ADMIN" || role === "AGENT";
  const isCustomer = role === "CUSTOMER";
  const isRewardsPage = location.pathname.startsWith("/rewards");
  const isNewAuthPage =
    featureFlags.enabled("login2.0") &&
    (location.pathname === "/login" || location.pathname === "/sign-up" || location.pathname === "/reset-password");

  const isNewCustomerUI = isCustomer && featureFlags.enabled("packages2.0");

  const theme = isNewAuthPage || isRewardsPage || isNewCustomerUI ? theme2 : theme1;
  const wrapInContainer = isRewardsPage || isNewCustomerUI;
  const appContainerNotFull = isNewAuthPage || isRewardsPage || isNewCustomerUI;

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <Stack className="app-container">
        {!isNewAuthPage && <AppBar userInfo={user ?? null} context={context} />}
        <ReadOnlyBanner />
        <div className={`app-container__content ${appContainerNotFull ? "app-container__content--full" : ""}`}>
          <MaybeContainer wrap={wrapInContainer}>
            <MuiThemeProvider theme={theme}>
              <WithAppUser user={user ?? null} loading={loading} context={context} forbiddenRoutes={forbiddenRoutes}>
                <Routes>
                  {PackagesRoutes({ role })}
                  <Route
                    path="public/orders/:orderId/participant-lists"
                    element={
                      <>
                        <PageTitle title="lista uczestników" />
                        <PublicParticipantListsPage />
                      </>
                    }
                  />
                  <Route path="/">
                    <Route index element={<Navigate to={isAdminOrAgent ? "tasks" : "orders"} />} />
                    <Route
                      path="login"
                      element={
                        <>
                          <PageTitle title="logowanie" />
                          {featureFlags.enabled("login2.0") ? <LoginChooser /> : <LongChooserOld />}
                        </>
                      }
                    />
                    <Route
                      path="sign-up"
                      element={
                        <>
                          <PageTitle title="rejestracja" />
                          {featureFlags.enabled("login2.0") ? <EmailPasswordSignUp /> : <EmailPasswordSignUpOld />}
                        </>
                      }
                    />
                    <Route
                      path="reset-password"
                      element={
                        <>
                          <PageTitle title="reset hasła" />
                          {featureFlags.enabled("login2.0") ? <ResetPassword /> : <ResetPasswordOld minWidth="550px" />}
                        </>
                      }
                    />
                    <Route path="orders">
                      <Route
                        index
                        element={
                          <>
                            <PageTitle title="lista zamówień" />
                            <>
                              {isNewCustomerUI ? (
                                <OrdersPage
                                  search={ordersSearch}
                                  onSearchChange={setOrdersSearch}
                                  onSearchClear={clearOrdersSearch}
                                />
                              ) : (
                                <OldOrdersPage
                                  search={ordersSearch}
                                  onSearchChange={setOrdersSearch}
                                  onSearchClear={clearOrdersSearch}
                                />
                              )}
                            </>
                          </>
                        }
                      />
                      <Route
                        path="new"
                        element={
                          <>
                            <PageTitle title="nowe zamówienie" />
                            <NewOrderPage />
                          </>
                        }
                      />
                      <Route path=":orderId">
                        <Route
                          index
                          element={
                            <>
                              <PageTitle title="szczegóły zamówienia" />
                              <OrderPage role={role!} />
                            </>
                          }
                        />
                        <Route
                          path="confirmations/:confirmationId"
                          element={
                            <>
                              <PageTitle title="potwierdzenie zamówienia" />
                              <OrderConfirmationPage />
                            </>
                          }
                        />
                        <Route
                          path="participant-lists"
                          element={
                            <>
                              <PageTitle title="lista uczestników zamówienia" />
                              <OrderParticipantLists />
                            </>
                          }
                        />
                        <Route
                          path="bookings"
                          element={
                            <>
                              <PageTitle title="dane rezerwacji do zamówienia" />
                              <OrderBookingPage />
                            </>
                          }
                        />
                      </Route>
                    </Route>
                    {featureFlags.enabled("packages2.0")
                      ? CalculationsRoutes2()
                      : CalculationsRoutes1({ calculationsSearch, setCalculationsSearch })}
                    {adminRoutes({ role, context, filters, onFiltersChange: handleFiltersChange })}
                    <Route path="costs">
                      <Route
                        index
                        element={
                          <>
                            <PageTitle title="lista kosztów" />
                            <CostsPage search={costsSearch} onSearchChange={setCostsSearch} />
                          </>
                        }
                      />
                      <Route
                        path="incomming-transfers"
                        element={
                          <>
                            <PageTitle title="Przelewy przychodzące" />
                            <IncomingTransfersPage />
                          </>
                        }
                      />
                      <Route
                        path=":costId"
                        element={
                          <>
                            <PageTitle title="szczegóły kosztu" />
                            <EditCostPage />
                          </>
                        }
                      />
                    </Route>
                    <Route path="e-commerce">
                      <Route
                        index
                        element={
                          <>
                            <PageTitle title="lista ofert e-commerce" />
                            {isNewCustomerUI ? <ECommerceOffersPage /> : <ECommerceOffersPageOld />}
                          </>
                        }
                      />
                      <Route
                        path=":eCommerceOfferId"
                        element={
                          <>
                            <PageTitle title="szczegóły oferty e-commerce" />
                            {isNewCustomerUI ? <ECommerceOfferPage /> : <ECommerceOfferPageOld />}
                          </>
                        }
                      />
                    </Route>
                    <Route
                      path="customers"
                      element={
                        <RequireCustomersAccess>
                          <Outlet />
                        </RequireCustomersAccess>
                      }
                    >
                      <Route
                        index
                        element={
                          <>
                            <PageTitle title="lista klientów" />
                            {featureFlags.enabled("klienci2.0") ? (
                              <AdminClientsList />
                            ) : (
                              <>
                                <CustomersList
                                  role={role!}
                                  search={customersSearch}
                                  onSearchChange={setCustomersSearch}
                                  onSearchClear={clearCustomersSearch}
                                />
                              </>
                            )}
                          </>
                        }
                      />
                      <Route path="new" element={<NewCustomerPage />} />
                      <Route
                        path="map"
                        element={
                          <>
                            <PageTitle title="mapa klientów" />
                            <CustomerMapPage />
                          </>
                        }
                      />
                      <Route path=":customerId" element={<EditCustomerPage />} />
                    </Route>
                    <Route path="tasks">
                      <Route
                        index
                        element={
                          <>
                            <PageTitle title="lista zadań" />
                            <TasksPage role={role!} />
                          </>
                        }
                      />
                    </Route>
                    <Route path="quotes">
                      <Route
                        index
                        element={
                          <>
                            <PageTitle title="lista wycen" />
                            <QuotesList />
                          </>
                        }
                      />
                      <Route
                        path=":quoteId"
                        element={
                          <>
                            <PageTitle title="wycena" />
                            <QuotePage />
                          </>
                        }
                      />
                    </Route>
                    <Route
                      path="media"
                      element={
                        <>
                          <PageTitle title="media" />
                          {isNewCustomerUI ? <MediaBank /> : <MediaBankOld />}
                        </>
                      }
                    />
                    <Route path="map" element={<ProductMap />} />
                    <Route path="rewards">
                      <Route
                        index
                        element={
                          <>
                            <PageTitle title="lista nagród" />
                            <Container maxWidth="xl" sx={{ mt: 4 }}>
                              <PublicRewardsList
                                search={publicRewardsSearch}
                                onSearchChange={(change) => setPublicRewardsSearch((s) => ({ ...s, ...change }))}
                                role={role}
                              />
                            </Container>
                          </>
                        }
                      />
                      <Route
                        path=":rewardId"
                        element={
                          <>
                            <PageTitle title="nagroda" />
                            <Container maxWidth="xl" sx={{ mt: 4 }}>
                              <PublicRewardPage role={role} />
                            </Container>
                          </>
                        }
                      />
                    </Route>
                    <Route path="maintenance" element={role === "ADMIN" ? <Maintenance /> : <h1>Forbidden</h1>} />
                    <Route path="nowy" element={<UI2SelectionPage />} />
                  </Route>
                  <Route path="/not-found" element={<GenericNotFound />} />
                  <Route path="*" element={<Navigate to="/not-found" replace />} />
                </Routes>
              </WithAppUser>
            </MuiThemeProvider>
          </MaybeContainer>
        </div>
      </Stack>
    </MuiThemeProvider>
  );
};
function PackagesRoutes(props: { role: UserRole | null }) {
  return featureFlags.enabled("packages2.0") ? PackagesRoutes2(props) : PackagesRoutes1(props);
}

export default App;
